import React, { useState } from 'react';
import { AlertTriangle, Loader2, ShieldAlert, Trash2, CheckSquare, Square } from 'lucide-react';
import { apiGet, apiPost } from '../lib/api';
import { toast } from 'sonner';

const PHRASE = 'WIPE ALL DATA ELLORA';

/**
 * Danger Zone — Wipe Operational Data
 *
 * Visible to super_admin only (caller already gates this).
 * Three-step confirmation:
 *   1. Click "Show data wipe preview" → /admin/wipe-preview (read-only).
 *   2. (Optional) Untick collections you want to KEEP. Defaults to all selected.
 *   3. Type the exact phrase + super-admin password → /admin/wipe-all-data.
 *
 * Iter 109 — Added per-collection checkboxes + "Select all" toggle so HR can
 * cherry-pick what gets deleted (e.g. wipe `projects` but keep `employees`).
 */
export default function DangerZone() {
  const [previewOpen, setPreviewOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [phrase, setPhrase] = useState('');
  const [password, setPassword] = useState('');
  const [wiping, setWiping] = useState(false);

  const loadPreview = async () => {
    setLoadingPreview(true);
    try {
      const r = await apiGet('/admin/wipe-preview');
      setPreview(r);
      // Default to ALL collections selected
      setSelected(new Set(Object.keys(r.collections || {})));
      setPreviewOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load preview');
    } finally {
      setLoadingPreview(false);
    }
  };

  const toggle = (name) => {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(name)) n.delete(name); else n.add(name);
      return n;
    });
  };
  const all = Object.keys(preview?.collections || {});
  const allSelected = all.length > 0 && all.every((c) => selected.has(c));
  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(all));
  };

  const submit = async () => {
    if (phrase !== PHRASE) { toast.error(`Type exactly: ${PHRASE}`); return; }
    if (!password) { toast.error('Password required'); return; }
    if (selected.size === 0) { toast.error('Select at least one collection to wipe'); return; }
    const targets = Array.from(selected);
    /* eslint-disable no-alert */
    if (!window.confirm(
      `FINAL CONFIRMATION\n\nWipe ${targets.length} collection${targets.length === 1 ? '' : 's'}?\n\n` +
      targets.slice(0, 12).join(', ') + (targets.length > 12 ? `, …and ${targets.length - 12} more` : '') +
      `\n\nThis cannot be undone. Continue?`
    )) return;
    /* eslint-enable no-alert */
    setWiping(true);
    try {
      const res = await apiPost('/admin/wipe-all-data', {
        confirmation_phrase: phrase,
        current_password: password,
        selected_collections: targets,
      });
      toast.success(res?.message || 'Wipe complete');
      setPhrase(''); setPassword(''); setPreview(null); setPreviewOpen(false); setSelected(new Set());
      setTimeout(() => window.location.reload(), 1500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Wipe failed');
    } finally { setWiping(false); }
  };

  const selectedDocCount = Object.entries(preview?.collections || {})
    .filter(([k]) => selected.has(k))
    .reduce((acc, [, n]) => acc + n, 0);

  return (
    <div className="mt-10 border-2 border-red-300 bg-red-50/40 rounded-sm p-6" data-testid="danger-zone">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-sm bg-red-100 text-red-600 flex items-center justify-center shrink-0">
          <ShieldAlert size={20} />
        </div>
        <div>
          <div className="font-display font-bold text-lg text-red-700 flex items-center gap-2">
            Danger Zone · Wipe Operational Data
            <span className="text-[10px] font-bold uppercase tracking-wider bg-red-600 text-white px-2 py-0.5 rounded">SUPER ADMIN ONLY</span>
          </div>
          <p className="text-sm text-slate-700 mt-1.5">
            Permanently delete records across selected collections — projects, BOQ, WBS, employees, attendance, payroll, CRM, tenders, procurement, inventory, MRP, equipment, finance, quality, etc. You can cherry-pick which collections to wipe.
          </p>
          <p className="text-xs text-slate-600 mt-2">
            <b>Always preserved:</b> your super_admin login, HR policies, holiday calendar, leave policies, shifts, statutory settings, CRM SLA config, target industries, app settings, audit logs.
          </p>
        </div>
      </div>

      {!previewOpen ? (
        <button
          onClick={loadPreview}
          disabled={loadingPreview}
          className="inline-flex items-center gap-2 px-4 py-2 border border-red-400 text-red-700 rounded-sm hover:bg-red-100 text-sm font-semibold disabled:opacity-50"
          data-testid="wipe-preview-btn"
        >
          {loadingPreview ? <Loader2 size={14} className="animate-spin" /> : <AlertTriangle size={14} />}
          Show data wipe preview
        </button>
      ) : (
        <div className="space-y-4">
          <CollectionPicker
            preview={preview}
            selected={selected}
            toggle={toggle}
            toggleAll={toggleAll}
            allSelected={allSelected}
            selectedDocCount={selectedDocCount}
          />

          <div className="bg-white border border-red-300 rounded-sm p-4 space-y-3">
            <div>
              <label className="block text-xs font-display font-bold uppercase tracking-wider text-slate-700 mb-1.5">
                Step 1 · Type exactly: <span className="font-mono text-red-700">{PHRASE}</span>
              </label>
              <input
                type="text"
                value={phrase}
                onChange={(e) => setPhrase(e.target.value)}
                placeholder={PHRASE}
                className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm font-mono focus:border-red-500 focus:outline-none"
                data-testid="wipe-phrase-input"
              />
            </div>
            <div>
              <label className="block text-xs font-display font-bold uppercase tracking-wider text-slate-700 mb-1.5">
                Step 2 · Re-enter your super_admin password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:border-red-500 focus:outline-none"
                data-testid="wipe-password-input"
              />
            </div>
            <div className="flex items-center gap-2 pt-1">
              <button
                onClick={submit}
                disabled={wiping || phrase !== PHRASE || !password || selected.size === 0}
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-red-600 text-white rounded-sm hover:bg-red-700 text-sm font-bold disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="wipe-confirm-btn"
              >
                {wiping ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                {wiping ? 'Wiping…' : `Wipe ${selected.size} collection${selected.size === 1 ? '' : 's'} (${selectedDocCount.toLocaleString('en-IN')} docs)`}
              </button>
              <button
                onClick={() => { setPreviewOpen(false); setPhrase(''); setPassword(''); setSelected(new Set()); }}
                disabled={wiping}
                className="px-4 py-2.5 text-sm font-semibold text-slate-600 hover:text-slate-900"
                data-testid="wipe-cancel-btn"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CollectionPicker({ preview, selected, toggle, toggleAll, allSelected, selectedDocCount }) {
  if (!preview) return null;
  const entries = Object.entries(preview.collections || {}).sort((a, b) => b[1] - a[1]);
  if (entries.length === 0) {
    return (
      <div className="bg-white border border-slate-200 rounded-sm p-4 text-sm text-slate-600">
        Nothing to delete. All operational collections are already empty.
      </div>
    );
  }
  return (
    <div className="bg-white border border-red-200 rounded-sm" data-testid="wipe-preview-panel">
      <div className="px-4 py-3 border-b border-red-200 flex items-center justify-between gap-3 flex-wrap bg-red-50/40">
        <div>
          <div className="font-display font-bold text-sm text-red-700">
            {selected.size} / {entries.length} collections selected · {selectedDocCount.toLocaleString('en-IN')} docs to delete
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5">
            Untick anything you want to keep (e.g. `employees` to preserve HR data when re-seeding projects only).
          </div>
        </div>
        <button
          type="button"
          onClick={toggleAll}
          className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-display font-bold uppercase tracking-wider border border-red-400 text-red-700 rounded-sm hover:bg-red-100"
          data-testid="wipe-select-all"
        >
          {allSelected ? <CheckSquare size={12} /> : <Square size={12} />}
          {allSelected ? 'Deselect all' : 'Select all'}
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-1 text-xs max-h-96 overflow-y-auto p-3">
        {entries.map(([name, n]) => {
          const on = selected.has(name);
          return (
            <label
              key={name}
              className={`flex items-center justify-between py-1.5 px-2 rounded-sm cursor-pointer border ${on ? 'bg-red-50 border-red-200' : 'bg-white border-slate-100 hover:bg-slate-50'}`}
              data-testid={`wipe-coll-${name}`}
            >
              <span className="flex items-center gap-2 min-w-0">
                <input
                  type="checkbox"
                  checked={on}
                  onChange={() => toggle(name)}
                  className="w-3.5 h-3.5 accent-red-600 cursor-pointer"
                />
                <span className="font-mono text-slate-700 truncate" title={name}>{name}</span>
              </span>
              <span className={`tabular-nums font-bold shrink-0 ${on ? 'text-red-600' : 'text-slate-400'}`}>{n}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
}
